import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const canvasRef = useRef(null);

  // ANIMATED NETWORK BACKGROUND - Same as Home
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    if (window.innerWidth < 768) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    class NetworkNode {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.8;
        this.vy = (Math.random() - 0.5) * 0.8;
        this.radius = Math.random() * 2.5 + 1;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        
        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;
        
        this.pulsePhase += 0.03;
      }

      draw() {
        const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;
        const size = this.radius + pulse * 1.5;
        
        ctx.shadowBlur = 8 + pulse * 4;
        ctx.shadowColor = '#00a8c0';
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, size);
        gradient.addColorStop(0, `rgba(0, 210, 230, ${0.6 + pulse * 0.15})`);
        gradient.addColorStop(0.5, `rgba(0, 180, 200, ${0.4 + pulse * 0.15})`);
        gradient.addColorStop(1, 'rgba(0, 150, 180, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    class FlowingLine {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.length = Math.random() * 100 + 50;
        this.angle = Math.random() * Math.PI * 2;
        this.speed = Math.random() * 2 + 1;
        this.life = 0;
        this.maxLife = Math.random() * 60 + 40;
      }

      update() {
        this.x += Math.cos(this.angle) * this.speed;
        this.y += Math.sin(this.angle) * this.speed;
        this.life++;
        
        if (this.life > this.maxLife || 
            this.x < -100 || this.x > canvas.width + 100 ||
            this.y < -100 || this.y > canvas.height + 100) {
          this.reset();
        }
      }

      draw() {
        const lifeRatio = this.life / this.maxLife;
        const opacity = Math.sin(lifeRatio * Math.PI) * 0.25;
        
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(
          this.x - Math.cos(this.angle) * this.length,
          this.y - Math.sin(this.angle) * this.length
        );
        
        const gradient = ctx.createLinearGradient(
          this.x, this.y,
          this.x - Math.cos(this.angle) * this.length,
          this.y - Math.sin(this.angle) * this.length
        );
        gradient.addColorStop(0, `rgba(0, 200, 220, ${opacity})`);
        gradient.addColorStop(1, 'rgba(0, 200, 220, 0)');
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.shadowBlur = 5;
        ctx.shadowColor = 'rgba(0, 180, 200, 0.2)';
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }

    const nodes = [];
    const nodeCount = 60;
    for (let i = 0; i < nodeCount; i++) {
      nodes.push(new NetworkNode());
    }

    const flowingLines = [];
    for (let i = 0; i < 12; i++) {
      flowingLines.push(new FlowingLine());
    }

    function drawConnections() {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 180) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            
            const opacity = (1 - distance / 180) * 0.2;
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            gradient.addColorStop(0, `rgba(0, 200, 180, ${opacity})`);
            gradient.addColorStop(1, `rgba(0, 180, 200, ${opacity})`);
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 1;
            ctx.shadowBlur = 3;
            ctx.shadowColor = 'rgba(0, 180, 200, 0.1)';
            ctx.stroke();
            ctx.shadowBlur = 0;
          }
        }
      }
    }

    function animate() {
      ctx.fillStyle = 'rgba(10, 37, 64, 0.12)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      flowingLines.forEach(line => {
        line.update();
        line.draw();
      });
      
      nodes.forEach(node => {
        node.update();
        node.draw();
      });
      drawConnections();
      animationFrameId = requestAnimationFrame(animate);
    }

    animate();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  const reasons = [
    {
      title: 'Market Research',
      description: 'Continuous research into emerging phishing tactics, data-driven market intelligence tailored to your sector.',
    },
    {
      title: 'Protect Every Device',
      description: 'Comprehensive security solutions safeguarding every device, everywhere.',
    },
    {
      title: 'User Experience',
      description: 'Delivering seamless solutions that empower user satisfaction and digital trust.',
    },
    {
      title: 'Protect Data Traffic',
      description: 'Safeguarding data in transit with advanced encryption and secure channels.',
    },
    {
      title: 'Remote Access',
      description: 'Enable secure and seamless remote access without compromising security.',
    },
    {
      title: 'Manage Access',
      description: 'Centralized access control to protect your systems while ensuring smooth operations.',
    },
  ];

  const team = [
    { 
      name: 'Dr. Gaurav Varshney', 
      role: 'Assistant Professor CSE', 
      experience: '15+ years in cybersecurity',
      image: '/team-member.jpg' // You'll need to add actual image
    },
  ];

  const stats = [
    { value: '50+', label: 'Security Experts' },
    { value: '1000+', label: 'Organizations Protected' },
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '24/7', label: 'Support Available' },
  ];

  const Card = ({ item, index }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="cyber-card text-center"
      >
        <h3 className="text-xl font-display font-semibold mb-3" style={{ color: '#00b4d8' }}>
          {item.title || item.name}
        </h3>
        <p className="text-gray-400">{item.description || item.role}</p>
        {item.experience && <p className="text-sm mt-2" style={{ color: '#00b4d8' }}>{item.experience}</p>}
      </motion.div>
    );
  };

  // Create separate component for stats to avoid hooks in callback
  const StatCard = ({ stat, index }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
    
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="relative group"
      >
        <div className="absolute inset-0 rounded-lg blur-xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: 'linear-gradient(135deg, #00b4d8 0%, #06ffa5 100%)' }}></div>
        <div className="relative glass-effect rounded-lg p-8 text-center">
          <h3 className="text-5xl font-display font-bold mb-2" style={{ color: '#00b4d8' }}>{stat.value}</h3>
          <p className="text-gray-400 font-medium">{stat.label}</p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #0d3a5c 35%, #115073 65%, #0d3a5c 100%)' }}>
      {/* Animated Background Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full pointer-events-none"
        style={{ zIndex: 1, height: '100%' }}
      />

      {/* Main Content */}
      <div className="relative pt-32 pb-20" style={{ zIndex: 10 }}>
      {/* Page Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 text-white">About Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Leading the fight against phishing and cyber threats worldwide
          </p>
        </motion.div>
      </section>


      

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 py-20" style={{ background: '#0d2847', borderRadius: '8px' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold tracking-wider uppercase mb-4 block" style={{ color: '#00b4d8' }}>
            OUR MISSION
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
            We Are Reliable In Terms Of Cyber Security
          </h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg leading-relaxed">
            <p>
              At Martianblue Cyber Defense, we protect you from the dangers of cyberspace. Our comprehensive 
              anti-phishing platform combines AI-powered threat detection, tactical cyber defense solutions, and 
              advanced analytics to safeguard your organization's digital assets. From research and development-based 
              antiphishing solutions to data leakage prevention, we deliver next-generation security that 
              adapts to evolving threats.
            </p>
            <p>
              Whether through secure software development, digital identity protection, or AI-powered cyber 
              defense consultancy, our commitment to your security is unwavering. We provide end-to-end 
              cybersecurity products, consulting services, and training programs that ensure your organization stays 
              secure, resilient, and prepared for tomorrow's challenges.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Testimonial */}


      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative glass-effect rounded-2xl p-12 text-center border-2" style={{ borderColor: 'rgba(0, 180, 216, 0.3)' }}
        >
          <h2 className="text-4xl font-display font-bold mb-6 text-white">Ready To Work With Us?</h2>
          <p className="text-xl text-gray-300 mb-8">Let's discuss how we can protect your organization</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="cyber-button text-lg px-8 py-4">Get In Touch</button>
            </Link>
            <Link to="/cybersecurity-services">
              <button 
                className="px-8 py-4 rounded-lg glass-effect border text-white hover:bg-white/10 transition-all duration-300 font-semibold"
                style={{ borderColor: 'rgba(0, 180, 216, 0.5)' }}
              >
                View Services
              </button>
            </Link>
          </div>
        </motion.div>
      </section>
        </div>
    </div>
  );
};

export default About;